import Phaser from "phaser";
import type { Socket } from "socket.io-client";

import {
  applyWorldCharacterSprite,
  worldCharacterTapRadiusPx,
  worldCharacterWalkBobY,
  WORLD_CHARACTER_BOB_SPEED,
} from "./world-character-sprite.js";
import { TILE_SIZE } from "@cold-case/shared/constants";
import {
  PEER_POSE_LERP_PER_MS,
  PEER_SNAP_DISTANCE_PX,
} from "@cold-case/shared/player-pose";
import type {
  GamePlayerPoseRelayPayload,
  GamePlayerSnapshotPayload,
} from "@cold-case/shared/player-pose";

import type { ZoneId } from "./map-grid.js";
import {
  interpolatePeerTarget,
  peerRenderAtMs,
  pushPoseSample,
  type PoseSample,
} from "./peer-interpolation.js";
import { velocityToPlayableDir, type PlayableDir } from "./playable-facing.js";
import type { PeerTapBlockedReason } from "../lib/peer-tap-blocked.js";
import {
  createWorldNameplateText,
  syncNameplateAboveSprite,
  type SessionPlayerLabel,
} from "./world-nameplate.js";

const PEER_TAP_HIT_RADIUS_PX = worldCharacterTapRadiusPx(0.88);

function parsePlayableDir(facing: string | undefined): PlayableDir {
  const dirs: PlayableDir[] = [
    "north",
    "south",
    "east",
    "west",
    "north-east",
    "north-west",
    "south-east",
    "south-west",
  ];
  if (facing && dirs.includes(facing as PlayableDir)) {
    return facing as PlayableDir;
  }
  return "south";
}

export type PeerState = {
  userId: string;
  rig: Phaser.GameObjects.Container;
  vis: Phaser.GameObjects.Sprite;
  nameplate: Phaser.GameObjects.Text;
  zone: ZoneId;
  /** When set, peer is inside this building (same outdoor zone). */
  interiorBuildingId: string | null;
  characterNpcId: string;
  lastDir: PlayableDir;
  poseBuffer: PoseSample[];
  lastPose?: PoseSample;
  bobPhase: number;
};

export type PeerSyncHost = {
  scene: Phaser.Scene;
  selfUserId: string;
  localZone: ZoneId;
  localInteriorBuildingId: string | null;
  peers: Map<string, PeerState>;
  sessionPlayers: readonly SessionPlayerLabel[];
  labelForUserId: (userId: string) => string;
  applyNpcTexture: (vis: Phaser.GameObjects.Sprite, skinId: string, dir: PlayableDir) => void;
  canTapPeer?: () => boolean;
  peerTapBlockedReason?: (peerUserId: string) => PeerTapBlockedReason | null;
  onPeerTap?: (userId: string) => void;
  onPeerTapBlocked?: (reason: PeerTapBlockedReason) => void;
};

function bindPeerPointer(host: PeerSyncHost, peer: PeerState): void {
  if (peer.rig.input) return;
  const hit = new Phaser.Geom.Circle(0, 0, PEER_TAP_HIT_RADIUS_PX);
  peer.rig.setInteractive(hit, Phaser.Geom.Circle.Contains);
  peer.rig.setDepth(16);
  peer.rig.on("pointerdown", (
    _pointer: Phaser.Input.Pointer,
    _lx: number,
    _ly: number,
    event: Phaser.Types.Input.EventData
  ) => {
    event.stopPropagation();
    if (host.canTapPeer?.()) {
      host.onPeerTap?.(peer.userId);
      return;
    }
    const reason = host.peerTapBlockedReason?.(peer.userId);
    if (reason) host.onPeerTapBlocked?.(reason);
  });
}

/** World-space peer pick when sprite hit areas miss (walk zone fallback). */
export function findPeerUserIdAtWorldPoint(
  host: PeerSyncHost,
  wx: number,
  wy: number,
  maxDistPx = 80
): string | null {
  let bestId: string | null = null;
  let bestD = maxDistPx;
  for (const peer of host.peers.values()) {
    if (!peerVisible(host, peer)) continue;
    const d = Math.hypot(peer.rig.x - wx, peer.rig.y - wy);
    if (d < bestD) {
      bestD = d;
      bestId = peer.userId;
    }
  }
  return bestId;
}

export function ensurePeerSprite(
  host: PeerSyncHost,
  userId: string,
  characterNpcId: string
): PeerState {
  let peer = host.peers.get(userId);
  if (peer) return peer;
  const rig = host.scene.add.container(0, 0);
  const vis = host.scene.add.sprite(0, 0, "placeholder_npc");
  applyWorldCharacterSprite(vis);
  const nameplate = createWorldNameplateText(
    host.scene,
    host.labelForUserId(userId),
    { localViewer: false }
  );
  rig.add(vis);
  rig.add(nameplate);
  syncNameplateAboveSprite(nameplate, vis);
  rig.setDepth(9);
  peer = {
    userId,
    rig,
    vis,
    nameplate,
    zone: "downtown",
    interiorBuildingId: null,
    characterNpcId,
    lastDir: "south",
    poseBuffer: [],
    bobPhase: 0,
  };
  host.peers.set(userId, peer);
  host.applyNpcTexture(vis, characterNpcId, "south");
  bindPeerPointer(host, peer);
  return peer;
}

export function getPeerRig(host: PeerSyncHost, userId: string): Phaser.GameObjects.Container | null {
  return host.peers.get(userId)?.rig ?? null;
}

export function getPeerFacing(host: PeerSyncHost, userId: string): PlayableDir | null {
  const peer = host.peers.get(userId);
  return peer?.lastDir ?? null;
}

export function seedPeersFromSession(
  host: PeerSyncHost,
  players: {
    userId: string;
    characterNpcId: string;
    zone?: string;
    tileX?: number;
    tileY?: number;
  }[]
): void {
  for (const p of players) {
    if (p.userId === host.selfUserId) continue;
    const peer = ensurePeerSprite(host, p.userId, p.characterNpcId);
    const zone: ZoneId =
      p.zone === "maple_heights" ? "maple_heights" : "downtown";
    peer.zone = zone;
    const tx = p.tileX ?? 0;
    const ty = p.tileY ?? 0;
    const px = tx * TILE_SIZE + TILE_SIZE / 2;
    const py = ty * TILE_SIZE + TILE_SIZE / 2;
    peer.rig.setPosition(px, py);
    const seed: PoseSample = {
      x: px,
      y: py,
      facing: peer.lastDir,
      seq: 0,
      receivedAtMs: host.scene.time.now,
    };
    peer.lastPose = seed;
    peer.poseBuffer = [seed];
    peer.interiorBuildingId = null;
    peer.rig.setVisible(peerVisible(host, peer));
    updatePeerDepth(peer);
  }
}

function peerVisible(host: PeerSyncHost, peer: PeerState): boolean {
  if (peer.zone !== host.localZone) return false;
  const localIn = host.localInteriorBuildingId;
  const peerIn = peer.interiorBuildingId;
  if (!localIn && !peerIn) return true;
  return localIn === peerIn;
}

export function clearAllPeers(host: PeerSyncHost): void {
  for (const userId of [...host.peers.keys()]) {
    removePeer(host, userId);
  }
}

export function removePeer(host: PeerSyncHost, userId: string): void {
  const peer = host.peers.get(userId);
  if (!peer) return;
  peer.rig.destroy();
  host.peers.delete(userId);
}

function updatePeerDepth(peer: PeerState): void {
  peer.rig.setDepth(5 + (peer.rig.x + peer.rig.y) * 0.02);
}

function applyPeerFacing(host: PeerSyncHost, peer: PeerState, facing: string): void {
  const dir = parsePlayableDir(facing);
  if (dir === peer.lastDir) return;
  peer.lastDir = dir;
  host.applyNpcTexture(peer.vis, peer.characterNpcId, dir);
}

function poseSampleFromRelay(
  payload: GamePlayerPoseRelayPayload,
  prevSeq: number,
  receivedAtMs: number
): PoseSample {
  const seq =
    typeof payload.seq === "number" && Number.isInteger(payload.seq) && payload.seq >= 0
      ? payload.seq
      : prevSeq + 1;
  return {
    x: payload.x,
    y: payload.y,
    facing: payload.facing,
    seq,
    receivedAtMs,
    serverSentAtMs: payload.serverSentAtMs,
  };
}

export function ingestPeerPose(
  host: PeerSyncHost,
  payload: GamePlayerPoseRelayPayload,
  characterNpcId: string
): void {
  if (payload.userId === host.selfUserId) return;
  const peer = ensurePeerSprite(host, payload.userId, characterNpcId);
  const prev = peer.lastPose;
  const sample = poseSampleFromRelay(
    payload,
    prev?.seq ?? -1,
    host.scene.time.now
  );
  if (prev && sample.seq <= prev.seq) return;

  peer.lastPose = sample;
  peer.poseBuffer = pushPoseSample(peer.poseBuffer, sample);
  peer.zone = payload.zone === "maple_heights" ? "maple_heights" : "downtown";
  if (payload.buildingId !== undefined) {
    const bid = payload.buildingId;
    peer.interiorBuildingId =
      typeof bid === "string" && bid.trim().length > 0 ? bid.trim() : null;
  }
  applyPeerFacing(host, peer, payload.facing);
  syncNameplateAboveSprite(peer.nameplate, peer.vis);
  peer.rig.setVisible(peerVisible(host, peer));
}

/** Leader exited a building — show followers at exit immediately on leader client. */
export function snapFollowersToLeaderExit(
  host: PeerSyncHost,
  followerUserIds: readonly string[],
  opts: { zone: ZoneId; wx: number; wy: number; buildingId: string | null }
): void {
  for (const uid of followerUserIds) {
    const peer = host.peers.get(uid);
    if (!peer) continue;
    peer.zone = opts.zone;
    peer.interiorBuildingId = opts.buildingId;
    peer.rig.setPosition(opts.wx, opts.wy);
    if (peer.lastPose) {
      peer.lastPose = { ...peer.lastPose, x: opts.wx, y: opts.wy };
    }
    peer.poseBuffer = [];
    peer.rig.setVisible(peerVisible(host, peer));
    updatePeerDepth(peer);
  }
}

/** @deprecated Use ingestPeerPose — kept for tests that still call tile payloads. */
export function updatePeerFromMove(
  host: PeerSyncHost,
  payload: {
    userId: string;
    tileX?: number;
    tileY?: number;
    x?: number;
    y?: number;
    zone: string;
    facing?: string;
    seq?: number;
    serverSentAtMs?: number;
  },
  characterNpcId: string
): void {
  const px =
    typeof payload.x === "number"
      ? payload.x
      : (payload.tileX ?? 0) * TILE_SIZE + TILE_SIZE / 2;
  const py =
    typeof payload.y === "number"
      ? payload.y
      : (payload.tileY ?? 0) * TILE_SIZE + TILE_SIZE / 2;
  ingestPeerPose(
    host,
    {
      sessionId: "",
      userId: payload.userId,
      x: px,
      y: py,
      zone: payload.zone === "maple_heights" ? "maple_heights" : "downtown",
      facing: payload.facing ?? "south",
      seq: payload.seq,
      serverSentAtMs: payload.serverSentAtMs ?? Date.now(),
    },
    characterNpcId
  );
}

export function applyPeerSnapshot(
  host: PeerSyncHost,
  payload: GamePlayerSnapshotPayload,
  playerCharacterByUserId: Map<string, string>
): void {
  for (const pose of payload.poses) {
    const skin = playerCharacterByUserId.get(pose.userId) ?? "police_officer_m";
    ingestPeerPose(host, pose, skin);
    const peer = host.peers.get(pose.userId);
    if (peer?.lastPose) {
      peer.rig.setPosition(peer.lastPose.x, peer.lastPose.y);
      updatePeerDepth(peer);
    }
  }
}

export function tickPeerInterpolation(host: PeerSyncHost, delta: number): void {
  const k = Math.min(1, PEER_POSE_LERP_PER_MS * delta);
  const renderAt = peerRenderAtMs(host.scene.time.now);

  for (const peer of host.peers.values()) {
    if (!peer.lastPose) continue;

    const { x: tx, y: ty } = interpolatePeerTarget(
      peer.poseBuffer,
      peer.lastPose,
      renderAt
    );

    const dist = Math.hypot(peer.rig.x - tx, peer.rig.y - ty);
    if (dist > PEER_SNAP_DISTANCE_PX) {
      peer.rig.setPosition(tx, ty);
    } else {
      const prevX = peer.rig.x;
      const prevY = peer.rig.y;
      peer.rig.x = Phaser.Math.Linear(peer.rig.x, tx, k);
      peer.rig.y = Phaser.Math.Linear(peer.rig.y, ty, k);
      const step = Math.hypot(peer.rig.x - prevX, peer.rig.y - prevY);
      if (step > 0.5) {
        const dir = velocityToPlayableDir(peer.rig.x - prevX, peer.rig.y - prevY);
        if (dir !== peer.lastDir) {
          peer.lastDir = dir;
          host.applyNpcTexture(peer.vis, peer.characterNpcId, dir);
        }
        peer.bobPhase += delta * WORLD_CHARACTER_BOB_SPEED;
        peer.vis.y = worldCharacterWalkBobY(peer.bobPhase, true, false);
      } else {
        peer.vis.y = 0;
      }
    }

    updatePeerDepth(peer);
    syncNameplateAboveSprite(peer.nameplate, peer.vis);
  }
}

export function setPeerZoneVisibility(
  host: PeerSyncHost,
  localZone: ZoneId,
  localInteriorBuildingId: string | null = null
): void {
  host.localZone = localZone;
  host.localInteriorBuildingId = localInteriorBuildingId;
  for (const peer of host.peers.values()) {
    peer.rig.setVisible(peerVisible(host, peer));
  }
}

export function bindPeerSync(
  socket: Socket | null,
  host: PeerSyncHost,
  playerCharacterByUserId: Map<string, string>
): void {
  if (!socket) return;
  socket.off(SOCKET_GAME_PLAYER_MOVE);
  socket.off(SOCKET_GAME_ZONE_CHANGE);
  socket.off(SOCKET_GAME_PLAYER_SNAPSHOT);
  socket.off(SOCKET_GAME_PLAYER_LEFT);

  socket.on(SOCKET_GAME_PLAYER_MOVE, (payload: GamePlayerPoseRelayPayload) => {
    const skin =
      playerCharacterByUserId.get(payload.userId) ?? "police_officer_m";
    ingestPeerPose(host, payload, skin);
  });
  socket.on(SOCKET_GAME_PLAYER_SNAPSHOT, (payload: GamePlayerSnapshotPayload) => {
    applyPeerSnapshot(host, payload, playerCharacterByUserId);
  });
  socket.on(SOCKET_GAME_PLAYER_LEFT, (payload: { userId: string }) => {
    if (!payload?.userId || payload.userId === host.selfUserId) return;
    removePeer(host, payload.userId);
  });
  socket.on(
    SOCKET_GAME_ZONE_CHANGE,
    (payload: { userId: string; zone: string; buildingId?: string | null }) => {
      if (payload.userId === host.selfUserId) return;
      const peer = host.peers.get(payload.userId);
      if (!peer) return;
      peer.zone = payload.zone === "maple_heights" ? "maple_heights" : "downtown";
      peer.interiorBuildingId =
        typeof payload.buildingId === "string" && payload.buildingId.length > 0
          ? payload.buildingId
          : null;
      peer.rig.setVisible(peerVisible(host, peer));
      if (!peer.rig.visible) {
        peer.vis.y = 0;
      }
    }
  );
}

const SOCKET_GAME_PLAYER_MOVE = "game:player_move";
const SOCKET_GAME_PLAYER_SNAPSHOT = "game:player_snapshot";
const SOCKET_GAME_PLAYER_LEFT = "game:player_left";
const SOCKET_GAME_ZONE_CHANGE = "game:zone_change";
